import React, { useEffect, useState } from "react";
import { MdAdd, MdAdminPanelSettings, MdDomain } from "react-icons/md";
import AddWhitelistDomainModal from "../../../components/modal/AddWhitelistDomainModal";
import getWhitelistDomains from "../../../controllers/settings/whitelist/getWhitelistDomains";
import { useSelector } from "react-redux";
import WhitelistDomainList from "./WhitelistDomainList";
import removeWhitelistDomain from "../../../controllers/settings/whitelist/removeWhitelistDomain";
import { enqueueSnackbar } from "notistack";

function WhitelistDomainContent({ onSet }) {
  const { adminAuth } = useSelector((state) => state.user);
  const [addDomain, setAddDomain] = useState(false);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    const res = await getWhitelistDomains(adminAuth).finally(() =>
      setLoading(false)
    );
    if (res?.return) {
      const items = res?.data || [];
      setDomains(items);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (origin) => {
    if (!adminAuth) {
      enqueueSnackbar("Admin authorization is required.", { variant: "error" });
      return;
    }

    const res = await removeWhitelistDomain(adminAuth, origin);

    if (res?.return) {
      enqueueSnackbar("Domain removed from whitelist.", { variant: "success" });
      loadData(); // 🔁 re-fetch domains
    } else {
      enqueueSnackbar(
        res?.msg || "Failed to remove domain. Please try again.",
        { variant: "error" }
      );
    }
  };

  return (
    <div className="w-full flex flex-col">
      <AddWhitelistDomainModal
        open={addDomain}
        setOpen={setAddDomain}
        onSave={(items) => setDomains(items)}
      />

      <div className="w-full flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <MdDomain className="text-primary text-xl" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-gray-900">
              Whitelist domains
            </span>
            <span className="text-sm text-gray-500 truncate">
              Manage which domains can access the system
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setAddDomain(true)}
            className="h-10 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-primary1 text-white hover:opacity-90 transition w-full sm:w-auto"
          >
            <MdAdd className="text-lg" />
            Add domain
          </button>

          <button
            type="button"
            onClick={() => onSet?.()}
            className="h-10 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-gray-100 text-gray-800 hover:bg-gray-200 transition w-full sm:w-auto"
          >
            <MdAdminPanelSettings className="text-lg" />
            Set auth
          </button>
        </div>
      </div>

      {loading ? (
        <DomainListShimmer />
      ) : (
        <WhitelistDomainList
          domains={domains}
          onDelete={async (domain) => await handleDelete(domain)}
        />
      )}
    </div>
  );
}

export default WhitelistDomainContent;

const DomainListShimmer = () => {
  return (
    <div className="rounded-2xl border border-outline bg-white divide-y animate-pulse">
      {[1, 2, 3].map((_, idx) => (
        <div key={idx} className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-9 h-9 rounded-lg bg-gray-200" />
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-3 w-2/3 bg-gray-200 rounded" />
              <div className="h-3 w-1/3 bg-gray-100 rounded" />
            </div>
          </div>
          <div className="w-16 h-8 bg-gray-200 rounded-md" />
        </div>
      ))}
    </div>
  );
};
